// 02 · Apports — la monnaie remise aux caissiers en début de journée.
//
// Portage intégral de l'écran V2 (ApportsClient.jsx) : parcours liste →
// caissier → saisie, huit dénominations de rouleaux plus les billets de 5 et
// 10 €, plomb, saisie ORIKA, message d'apport, total par caissier et du jour.
//
// La valorisation n'est PAS recodée ici. Le rouleau de 10 c vaut 4,00 € et
// celui de 1 € 25 pièces — deux exceptions bancaires qui ne se devinent pas et
// qu'un second jeu de constantes finirait par contredire. Elles vivent une
// seule fois, dans sotcp-core/inventaireCoffreCalc, avec la somme elle-même.
//
// La clé d'un apport reste « Prénom Nom », comme en V2 : c'est elle qui est
// déjà écrite dans rapport.apports de toutes les journées passées, et c'est
// elle que save_apport attend en p_op_id.
//
// La saisie passe par un BROUILLON local (§ « Brouillon de saisie ») : les
// compteurs partaient sinon en une RPC par frappe et par clic. Le total suit
// les doigts, le réseau attend la fin du geste.
//
// Pas de module Honeywell sur cette étape dans ce build (point ouvert O3) :
// l'enveloppe s'en charge, cet écran n'en parle pas.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { APPORT_ROULEAUX, APPORT_BILLETS, sumApport } from 'sotcp-core/inventaireCoffreCalc'
import * as api from '@/data/api'
import { useRun } from '@/data/RunProvider'
import { useReglages, aUnFondsDeCaisse, type Caissier } from '@/data/reglages'
import { couleurEcartUnites, fmtEcartUnites } from '@/domain/ecart'
import { htmlPapierApports } from '@/pdf/apportsPaper'
import { imprimerDocument } from '@/pdf/imprimer'
import { fmtEur, pluriel } from '@/lib/format'
import { Card, Chantier, Chip, Kpis, Lamp, Modale, Notice, Vide, type Couleur } from '@/ui/primitives'
import { useToast } from '@/ui/Toast'
import type { PropsEtape } from './types'

type Brut = Record<string, unknown>

/** Le geste est fini quand les doigts s'arrêtent : au-delà, l'écriture part. */
const DELAI_ECRITURE = 350

/**
 * @/domain/ecart donne la COULEUR d'un écart en nombre, pas sa lampe. Miroir
 * local des mêmes seuils (0 conforme, 1 à surveiller, 2 et plus critique) —
 * à remonter dans le module partagé, qui est hors limites ici.
 */
const lampeEcartUnites = (n: number): Couleur => {
  const a = Math.abs(n)
  return a === 0 ? 'ok' : a === 1 ? 'warn' : 'stop'
}

const objet = (v: unknown): Brut =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Brut) : {}

const texte = (v: unknown): string => (typeof v === 'string' ? v : '')

/** Convention V2 intacte : la clé d'un apport est « Prénom Nom ». */
const cleApport = (op: Caissier): string => [op.prenom, op.nom].filter(Boolean).join(' ')

const qte = (source: unknown, id: string): number => {
  const n = Number(objet(source)[id])
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
}

const totalApport = (d: Brut): number =>
  sumApport(d as { rouleaux?: Record<string, unknown>; billets?: Record<string, unknown> })

/** Le repère de la liste V2 : combien de dénominations différentes ont été servies. */
const nbDenominations = (d: Brut): number =>
  APPORT_ROULEAUX.filter((r) => qte(d.rouleaux, r.id) > 0).length
  + APPORT_BILLETS.filter((b) => qte(d.billets, b.id) > 0).length

const entier = (s: string): number => {
  const n = parseInt(s.replace(/\D/g, ''), 10)
  return Number.isFinite(n) && n > 0 ? n : 0
}

/**
 * Empreinte canonique d'un apport : clés triées, quantités nulles et champs
 * vides retirés.
 *
 * Le brouillon compare l'avant et l'après pour ne rien écrire quand une rafale
 * revient à son point de départ. Comparé en JSON brut, « + » puis « − » sur une
 * dénomination jamais servie laissait un `{"2c":0}` là où le serveur n'avait
 * rien, et Échap sur un plomb vierge un `plomb:""` — deux objets que
 * JSON.stringify déclare différents, donc deux écritures (avec leur
 * `toucherEtape` et leur risque de rollback) pour un apport inchangé. Un zéro
 * absent et un zéro écrit disent la même chose : `qte` les lit pareil.
 */
const empreinte = (v: unknown): string => {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const parts = Object.entries(v as Brut)
      .map(([k, x]) => [k, empreinte(x)] as const)
      .filter(([, s]) => s !== '')
      .sort((a, b) => a[0].localeCompare(b[0]))
    return parts.length === 0 ? '' : `{${parts.map(([k, s]) => `${k}:${s}`).join(',')}}`
  }
  if (v === null || v === undefined || v === '' || v === 0) return ''
  return String(v)
}

export default function Apports({ rapport, lectureSeule }: PropsEtape) {
  const { date, journee, ecrireRapport } = useRun()
  const toast = useToast()
  const { caissiers, caisseFixes, badgesSansFdc } = useReglages()

  const [vue, setVue] = useState<'liste' | 'choix' | 'saisie'>('liste')
  const [cleEnCours, setCleEnCours] = useState<string | null>(null)
  const [recherche, setRecherche] = useState('')
  const [aRetirer, setARetirer] = useState<string | null>(null)
  const [aEffacer, setAEffacer] = useState<string | null>(null)
  const [travail, setTravail] = useState(false)

  const source = rapport?.apports
  const apports = useMemo(() => objet(source), [source])

  // Les réglages arrivent après le premier rendu : tant qu'ils sont vides, un
  // apport n'est pas « inconnu aux réglages », il est seulement en attente.
  const reglagesCharges = caissiers.length > 0

  const lignes = useMemo(() => Object.keys(apports)
    .sort((a, b) => a.localeCompare(b, 'fr'))
    .map((cle) => {
      const brut = objet(apports[cle])
      const op = caissiers.find((c) => cleApport(c) === cle)
      return {
        cle,
        badge: op ? String(op.badge) : null,
        // Un apport saisi pour quelqu'un qui n'a pas de fonds de caisse est une
        // anomalie qui mérite d'être vue, pas d'être empêchée après coup.
        sansFdc: op ? !aUnFondsDeCaisse(op, badgesSansFdc) : false,
        brut,
        total: totalApport(brut),
        plomb: texte(brut.plomb),
        saisie: texte(brut.saisie),
        denoms: nbDenominations(brut),
      }
    }), [apports, badgesSansFdc, caissiers])

  // Les monnayeurs des caisses automatiques sont servis à l'étape 02A, avant
  // les caissiers. Ils s'affichent ici et s'impriment sur le même papier : c'est
  // la même sortie de coffre, le même matin, le même bordereau. Ils vivent sous
  // `apportsSco` et non dans `apports` — cf. D21.
  const sco = useMemo(() => {
    const src = objet(journee?.rapport?.apportsSco)
    return Object.keys(src)
      .map((k) => objet(src[k]))
      .sort((a, b) => (Number(a.caisse) || 0) - (Number(b.caisse) || 0))
  }, [journee?.rapport])
  const totalSco = sco.reduce((s, m) => s + (Number(m.valeur) || 0), 0)

  const totalCaissiers = lignes.reduce((s, l) => s + l.total, 0)
  const totalJour = totalCaissiers + totalSco
  // Saisir l'apport dans ORIKA n'est PAS le travail du poste : cela n'arrive
  // que dans des cas particuliers. C'est donc une exception qu'on signale
  // quand elle a eu lieu, pas une case à cocher qui manquerait au reste.
  const orikaSaisis = lignes.filter((l) => l.saisie === 'OK').length
  const avecPlomb = lignes.filter((l) => l.plomb).length
  const totalFixes = caisseFixes.reduce((s, f) => s + (Number(f.default_amount) || 0), 0)
  const attendus = caissiers.filter((c) => aUnFondsDeCaisse(c, badgesSansFdc)).length

  // ── Écriture ─────────────────────────────────────────────────────────────
  // save_apport est atomique par caissier : deux personnes qui servent deux
  // caissiers différents ne s'écrasent plus à travers l'objet `apports` entier.
  const ecrireApport = useCallback(async (cle: string, valeur: Brut | null) => {
    // Dernier rempart : une étape validée ou une journée clôturée ne s'écrit
    // plus, quel que soit le chemin (compteur, plomb, ORIKA, effacement,
    // retrait) et même si l'écran est devenu lecture seule pendant la saisie.
    if (lectureSeule) return
    const suivant: Brut = { ...objet(source) }
    if (valeur === null) delete suivant[cle]
    else suivant[cle] = valeur
    try {
      await ecrireRapport(
        valeur === null ? `retrait de l’apport de ${cle}` : `apport de ${cle}`,
        { apports: suivant },
        () => api.enregistrerApport(date, cle, valeur),
        '02',
      )
    } catch { /* ecrireRapport a déjà remis l'état d'avant et dit pourquoi */ }
  }, [date, ecrireRapport, lectureSeule, source])

  // ── Brouillon de saisie ─────────────────────────────────────────────────
  // Chaque frappe et chaque clic sur « + » partaient en RPC : servir 40 rouleaux
  // sur huit dénominations, c'était une vingtaine d'allers-retours, plus autant
  // de `toucherEtape`. Le brouillon garde le geste en local — les totaux
  // bougent sous les doigts — et l'écriture part une fois, à la fin.
  const [brouillon, setBrouillon] = useState<{ cle: string; d: Brut } | null>(null)
  const tempoApport = useRef<number | undefined>(undefined)
  const enAttenteApport = useRef<{ cle: string; d: Brut } | null>(null)

  const enregistre = cleEnCours ? objet(apports[cleEnCours]) : {}
  const enCours = brouillon && brouillon.cle === cleEnCours ? brouillon.d : enregistre

  const poserApport = useCallback(() => {
    window.clearTimeout(tempoApport.current)
    const p = enAttenteApport.current
    enAttenteApport.current = null
    setBrouillon(null)
    if (!p) return
    // Une rafale qui revient à son point de départ (« + » puis « − », Échap
    // après une frappe) n'a rien à dire au serveur.
    if (empreinte(objet(objet(source)[p.cle])) === empreinte(p.d)) return
    void ecrireApport(p.cle, p.d)
  }, [ecrireApport, source])

  // `poserApport` change à chaque rendu ; le garder dans un ref évite qu'un
  // démontage ou un minuteur n'emporte le dernier comptage.
  const dernierPoser = useRef(poserApport)
  useEffect(() => { dernierPoser.current = poserApport })
  useEffect(() => () => {
    window.clearTimeout(tempoApport.current)
    dernierPoser.current()
  }, [])

  /**
   * Une modification de l'apport ouvert. `differe` : la frappe et les clics de
   * comptage attendent 350 ms, un choix explicite (ORIKA) part tout de suite.
   */
  const modifier = useCallback((patch: Brut, differe: boolean) => {
    if (!cleEnCours || lectureSeule) return
    const attente = { cle: cleEnCours, d: { ...enCours, ...patch } }
    window.clearTimeout(tempoApport.current)
    enAttenteApport.current = attente
    if (!differe) { dernierPoser.current(); return }
    setBrouillon(attente)
    tempoApport.current = window.setTimeout(() => dernierPoser.current(), DELAI_ECRITURE)
  }, [cleEnCours, enCours, lectureSeule])

  /** Remplacement en bloc (remise à zéro, retrait) : rien à fusionner, rien à différer. */
  const remplacer = useCallback((cle: string, d: Brut | null) => {
    window.clearTimeout(tempoApport.current)
    enAttenteApport.current = null
    setBrouillon(null)
    return ecrireApport(cle, d)
  }, [ecrireApport])

  // ── Message d'apport — frappe immédiate, écriture différée ───────────────
  const messageDistant = texte(rapport?.apportsMessage)
  const [message, setMessage] = useState(messageDistant)
  const tempo = useRef<number | undefined>(undefined)
  const enAttente = useRef<string | null>(null)

  const envoyerMessage = useCallback((val: string) => {
    enAttente.current = null
    // Même rempart que pour les apports : si l'étape s'est fermée pendant les
    // 400 ms de tempo, la frappe est abandonnée et le champ revient au serveur.
    if (lectureSeule) { setMessage(messageDistant); return }
    void ecrireRapport('message d’apport', { apportsMessage: val },
      () => api.patcherRapport(date, { apportsMessage: val }), '02').catch(() => {})
  }, [date, ecrireRapport, lectureSeule, messageDistant])

  // Le champ suit le serveur tant qu'on n'y écrit pas : à deux sur la même
  // journée, personne ne se fait voler son curseur.
  useEffect(() => { if (enAttente.current === null) setMessage(messageDistant) }, [messageDistant])

  // ecrireRapport change à chaque écriture du rapport ; garder l'envoi dans un
  // ref évite que le démontage n'emporte la dernière frappe.
  const dernierEnvoi = useRef(envoyerMessage)
  useEffect(() => { dernierEnvoi.current = envoyerMessage })
  useEffect(() => () => {
    window.clearTimeout(tempo.current)
    if (enAttente.current !== null) dernierEnvoi.current(enAttente.current)
  }, [])

  const saisirMessage = (val: string) => {
    if (lectureSeule) return
    setMessage(val)
    enAttente.current = val
    window.clearTimeout(tempo.current)
    tempo.current = window.setTimeout(() => envoyerMessage(val), 400)
  }

  const poserMessage = () => {
    window.clearTimeout(tempo.current)
    if (enAttente.current !== null) envoyerMessage(enAttente.current)
  }

  // ── Choix du caissier ────────────────────────────────────────────────────
  const servis = useMemo(() => new Set(Object.keys(apports)), [apports])
  const q = recherche.trim().toLowerCase()
  const disponibles = caissiers
    .filter((op) => !servis.has(cleApport(op)))
    .filter((op) => !q
      || cleApport(op).toLowerCase().includes(q)
      || String(op.badge).toLowerCase().includes(q))
    .sort((a, b) => {
      // Ceux qui ont un fonds de caisse à recevoir d'abord : on ne fait pas
      // défiler la liste des « SANS ESPÈCES » pour trouver qui servir.
      const fa = aUnFondsDeCaisse(a, badgesSansFdc) ? 0 : 1
      const fb = aUnFondsDeCaisse(b, badgesSansFdc) ? 0 : 1
      return fa - fb || a.nom.localeCompare(b.nom, 'fr')
    })

  // Toute sortie de la saisie pose d'abord ce qui restait en attente : la liste
  // ne doit jamais afficher un total plus vieux que le dernier geste.
  const ouvrirSaisie = (cle: string) => {
    dernierPoser.current()
    setCleEnCours(cle); setRecherche(''); setVue('saisie')
  }
  const ouvrirChoix = () => { dernierPoser.current(); setRecherche(''); setVue('choix') }
  const retourListe = () => { dernierPoser.current(); setCleEnCours(null); setRecherche(''); setVue('liste') }

  // ── Saisie d'un caissier ─────────────────────────────────────────────────
  const opEnCours = cleEnCours ? caissiers.find((c) => cleApport(c) === cleEnCours) : undefined
  const totalRouleaux = APPORT_ROULEAUX.reduce((s, r) => s + qte(enCours.rouleaux, r.id) * r.valeurRouleau, 0)
  const totalBillets = APPORT_BILLETS.reduce((s, b) => s + qte(enCours.billets, b.id) * b.denomination, 0)

  const majRouleau = (id: string, v: number) =>
    modifier({ rouleaux: { ...objet(enCours.rouleaux), [id]: v } }, true)
  const majBillet = (id: string, v: number) =>
    modifier({ billets: { ...objet(enCours.billets), [id]: v } }, true)

  const plombAuFocus = useRef('')

  /**
   * Remise à zéro : l'objet est remplacé en bloc. La saisie ORIKA repart à
   * « jamais répondu » et NON à « KO » — remettre un comptage à zéro n'est pas
   * répondre « pas encore » à la place de celui qui va le refaire. La V2
   * écrivait KO parce qu'elle n'avait que deux états ; ici la clé vide est un
   * état à part entière, et la ligne affiche « À RÉPONDRE » plutôt qu'un
   * « PAS SAISIE » rouge que personne n'a prononcé.
   */
  const effacer = async () => {
    if (!aEffacer || travail) return
    setTravail(true)
    await remplacer(aEffacer, { rouleaux: {}, billets: {}, plomb: '', saisie: '' })
    setTravail(false)
    setAEffacer(null)
  }

  const retirer = async () => {
    if (!aRetirer || travail) return
    setTravail(true)
    const cle = aRetirer
    await remplacer(cle, null)
    setTravail(false)
    setARetirer(null)
    if (cleEnCours === cle) { setCleEnCours(null); setVue('liste') }
  }

  // ══ VUE : choisir un caissier ═══════════════════════════════════════════
  // Le choix d'un caissier n'a de sens que pour servir : en lecture seule, on
  // retombe sur la liste plutôt que d'offrir un « Servir » qui n'écrira rien.
  if (vue === 'choix' && !lectureSeule) {
    return (
      <Card
        titre="Choisir un caissier à servir"
        actions={<button className="btn sm" onClick={retourListe}>↩ Retour à la liste</button>}
      >
        <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--line)' }}>
          <input
            className="fld" autoFocus value={recherche}
            placeholder="Nom, prénom ou badge — Entrée pour servir le premier…"
            onChange={(e) => setRecherche(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Escape') { retourListe(); return }
              // Entrée sert le premier de la liste filtrée : trois lettres et
              // c'est parti. Sans filtre, elle ne fait rien — « le premier de
              // trente » n'est pas un choix.
              if (e.key === 'Enter' && q) {
                const premier = disponibles[0]
                if (premier) ouvrirSaisie(cleApport(premier))
              }
            }}
          />
          <div className="hint" style={{ marginTop: 6 }}>
            {caissiers.length === 0
              ? 'Aucun caissier dans les réglages SotCP.'
              : `${pluriel(disponibles.length, 'caissier')} sans apport saisi.`}
          </div>
        </div>

        {disponibles.length === 0
          ? <Vide>{recherche
              ? 'Aucun caissier ne correspond.'
              : 'Tous les caissiers connus ont déjà un apport.'}</Vide>
          : disponibles.map((op) => {
            const cle = cleApport(op)
            const fdc = aUnFondsDeCaisse(op, badgesSansFdc)
            return (
              <div className="slot" key={`${op.badge}-${cle}`}>
                <Lamp c={fdc ? 'act' : 'neutre'} />
                <div style={{ minWidth: 0 }}>
                  <div className="nm">{cle || '—'}</div>
                  <div className="hint mo">
                    badge {String(op.badge)}{op.secteur ? ` · ${op.secteur}` : ''}
                  </div>
                </div>
                <span className="sp" />
                {!fdc && <Chip c="warn">SANS ESPÈCES</Chip>}
                {/* Neutre, et non « pri » : trente lignes de choix équivalents ne
                    peuvent pas porter trente actions principales. */}
                <button className="btn sm" onClick={() => ouvrirSaisie(cle)}>Servir</button>
              </div>
            )
          })}

        <div className="hint" style={{ padding: '8px 12px', borderTop: '1px solid var(--line)' }}>
          Un caissier « SANS ESPÈCES » n’a pas de fonds de caisse à recevoir — il reste
          servable, mais il n’est pas proposé en tête.
        </div>
      </Card>
    )
  }

  // ══ VUE : saisie d'un apport ════════════════════════════════════════════
  if (vue === 'saisie' && cleEnCours) {
    const total = totalRouleaux + totalBillets
    // Trois états, pas deux : la clé absente veut dire « jamais répondu », elle
    // ne vaut pas un « KO » assumé (convention V2 tenue partout ailleurs).
    const orika = texte(enCours.saisie)
    const plomb = texte(enCours.plomb)
    const complet = total > 0 && plomb !== '' && orika === 'OK'
    // Le vert ne dit qu'une chose : cet apport est complet. Un apport commencé
    // mais sans plomb ni saisie ORIKA n'est pas « conforme ».
    const lampe: Couleur = total === 0 ? 'act' : complet ? 'ok' : 'warn'
    const vierge = total === 0 && plomb === '' && orika === ''
    // Servir quelqu'un qui n'a encore rien reçu : les doigts sont déjà sur le
    // clavier, le premier rouleau prend la main.
    const nouveau = !lectureSeule && vierge

    return (
      <>
        {/* L'en-tête COLLE sous le bandeau de run (même repère que la barre de
            saisie du dépouillement, top 42). Dix rouleaux, deux billets et le
            contrôle font une page plus haute que l'écran : sans ça, le total
            de l'apport et « Servir le caissier suivant » sortent du champ dès
            le premier rouleau, et chaque caissier se termine par un
            défilement vers le haut pour retrouver le bouton. */}
        <div className="notice" style={{
          alignItems: 'center', position: 'sticky', top: 42, zIndex: 12,
        }}>
          <Lamp c={lampe} />
          <div className="sp">
            <b>{cleEnCours}</b>
            <span className="hint mo" style={{ marginLeft: 8 }}>
              badge {opEnCours ? String(opEnCours.badge) : '—'}
            </span>
          </div>
          <span className="mo" style={{ fontSize: 15 }}>{fmtEur(total)}</span>
          {/* Rien à effacer sur un apport encore vierge : le bouton n'apparaît
              que lorsqu'il y a quelque chose à perdre. */}
          {!lectureSeule && !vierge && (
            <button className="btn stop sm" onClick={() => setAEffacer(cleEnCours)}>
              Effacer la saisie
            </button>
          )}
          <button className="btn sm" onClick={retourListe}>Retour à la liste</button>
          {!lectureSeule && (
            <button className="btn pri sm" onClick={ouvrirChoix}>Servir le caissier suivant</button>
          )}
        </div>

        <div data-compteurs style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(316px,1fr))',
          gap: 14, alignItems: 'start',
        }}>
          <Card
            titre="Rouleaux"
            actions={<span className="mo" style={{ fontSize: 11 }}>{fmtEur(totalRouleaux)}</span>}
          >
            {APPORT_ROULEAUX.map((r, i) => (
              <LigneDenom
                key={r.id} etiquette={r.label} sous={r.sub} unite={r.valeurRouleau}
                quantite={qte(enCours.rouleaux, r.id)} lectureSeule={lectureSeule}
                auto={nouveau && i === 0} poser={poserApport}
                changer={(v) => majRouleau(r.id, v)}
              />
            ))}
          </Card>

          <div style={{ display: 'grid', gap: 14 }}>
            <Card
              titre="Billets"
              actions={<span className="mo" style={{ fontSize: 11 }}>{fmtEur(totalBillets)}</span>}
            >
              {APPORT_BILLETS.map((b) => (
                <LigneDenom
                  key={b.id} etiquette={b.label} sous={b.sub} unite={b.denomination}
                  quantite={qte(enCours.billets, b.id)} lectureSeule={lectureSeule}
                  poser={poserApport}
                  changer={(v) => majBillet(b.id, v)}
                />
              ))}
            </Card>

            <Card titre="Contrôle" lampe={plomb ? 'ok' : 'warn'}>
              <div className="slot">
                <div style={{ minWidth: 0 }}>
                  <div className="nm">Plomb n°</div>
                  <div className="hint">Numéro du plomb de sécurité de la sacoche</div>
                </div>
                <span className="sp" />
                <input
                  className="fld mo" inputMode="numeric" data-compteur value={plomb}
                  disabled={lectureSeule} placeholder="—"
                  style={{ width: 120, textAlign: 'center' }}
                  onFocus={(e) => { plombAuFocus.current = plomb; e.currentTarget.select() }}
                  onChange={(e) => modifier({ plomb: e.target.value.replace(/\D/g, '').slice(0, 12) }, true)}
                  onBlur={poserApport}
                  onKeyDown={(e) => {
                    e.stopPropagation()
                    // Entrée pose le plomb SANS rendre le focus : un blur() sec
                    // renvoyait le curseur sur <body>, et la question ORIKA —
                    // qui suit immédiatement — se retrouvait à une dizaine de
                    // Tab depuis le haut du document. Le champ garde la main,
                    // un Tab atteint « Saisie faite ». Échap rend le numéro
                    // d'avant.
                    if (e.key === 'Enter') poserApport()
                    else if (e.key === 'Escape') {
                      modifier({ plomb: plombAuFocus.current }, true)
                      e.currentTarget.blur()
                    }
                  }}
                />
              </div>
              <div className="slot">
                <div style={{ minWidth: 0 }}>
                  <div className="nm">Saisi dans ORIKA</div>
                  {/* Le poste ne saisit pas les apports dans ORIKA : ça n'arrive
                      que dans des cas particuliers. L'interrupteur est donc
                      éteint par défaut, et son extinction n'est pas un oubli. */}
                  <div className="hint">Cas particulier — à cocher seulement si tu l’as fait</div>
                </div>
                <span className="sp" />
                <button
                  className="btn act sm" aria-pressed={orika === 'OK'} disabled={lectureSeule}
                  onClick={() => modifier({ saisie: orika === 'OK' ? '' : 'OK' }, false)}>
                  {orika === 'OK' ? 'Saisi dans ORIKA' : 'Marquer comme saisi'}
                </button>
              </div>
            </Card>

            <Card titre="Récapitulatif">
              <div className="slot"><span className="nm">Rouleaux</span><span className="sp" />
                <span className="mo">{fmtEur(totalRouleaux)}</span></div>
              <div className="slot"><span className="nm">Billets</span><span className="sp" />
                <span className="mo">{fmtEur(totalBillets)}</span></div>
              <div className="slot"><span className="nm">Total de l’apport</span><span className="sp" />
                <span className="big">{fmtEur(total)}</span></div>
              <div className="hint" style={{ padding: '8px 12px', borderTop: '1px solid var(--line)' }}>
                Valorisation bancaire : le rouleau de 10 c vaut 4,00 € et celui de 1 € contient
                25 pièces. Les mêmes constantes servent à l’inventaire de coffre.
                {!lectureSeule && ' Entrée enchaîne les compteurs jusqu’au plomb, Échap annule la frappe.'}
              </div>
            </Card>
          </div>
        </div>

        {aEffacer && <ModaleEffacer
          cle={aEffacer} travail={travail}
          fermer={() => setAEffacer(null)} confirmer={effacer}
        />}
      </>
    )
  }

  // ══ VUE : la liste du jour ══════════════════════════════════════════════
  /**
   * Le papier d'apport — le formulaire que les caissiers signent.
   *
   * La V2 le produisait dans une route Next qui ne faisait PAS de PDF : elle
   * renvoyait un HTML autonome que le navigateur imprimait. Le générateur est
   * repris tel quel (`@/pdf/apportsPaper`), il ne lui manquait que ses données —
   * FlashCP les a déjà en mémoire, donc plus besoin de serveur.
   */
  const imprimerPapier = () => {
    const html = htmlPapierApports({
      date,
      rapport: (journee?.rapport ?? null) as Record<string, unknown> | null,
      caissiers,
      // La date du document est celle de sa PUBLICATION, pas celle de la journée
      // traitée : une journée clôturée réimprimée garde la date de sa clôture.
      clotureeAt: journee?.closed_at ?? null,
    })
    if (imprimerDocument(html, `Apports monnaie — ${date}`) === 'bloque') {
      toast('LE NAVIGATEUR A BLOQUÉ LA FENÊTRE D’IMPRESSION', true)
    }
  }

  const vide = lignes.length === 0
  const manquePlomb = lignes.length - avecPlomb
  // Le seul manque qui compte : le plomb. L'absence de saisie ORIKA est le cas
  // NORMAL — la compter comme un manque faisait clignoter l'écran tous les jours.
  const manque = manquePlomb

  return (
    <>
      <Kpis items={[
        {
          k: 'Caissiers servis', v: lignes.length,
          s: attendus > 0 ? `${attendus} avec fonds de caisse aux réglages` : undefined,
        },
        {
          k: 'Total du jour', v: fmtEur(totalJour),
          s: sco.length > 0
            ? `dont ${fmtEur(totalSco)} de monnayeurs SCO`
            : undefined,
        },
        // « 0 sur 0 » n'est pas une concordance : sans aucun apport saisi, ces
        // deux compteurs n'ont rien constaté et ne prennent aucune couleur.
        {
          k: 'Saisis dans ORIKA', v: vide ? '—' : orikaSaisis,
          s: vide ? undefined : (orikaSaisis > 0 ? 'cas particuliers' : 'cas normal'),
        },
        {
          k: 'Plombs relevés', v: vide ? '—' : `${avecPlomb} / ${lignes.length}`,
          s: vide ? undefined : fmtEcartUnites(-manquePlomb, 'apport'),
          couleur: vide ? undefined : couleurEcartUnites(manquePlomb),
        },
      ]} />

      {!vide && manque > 0 && (
        <Notice c={lampeEcartUnites(manque)}>
          <b>Il reste des apports sans plomb.</b>{' '}
          {`${pluriel(manquePlomb, 'apport')} sans numéro de plomb. `}
          Rien ne bloque la validation — mais le plomb est la seule trace de qui a scellé la sacoche.
        </Notice>
      )}

      {sco.length > 0 && (
        <Card
          titre="Monnayeurs SCO"
          lampe="ok"
          style={{ marginBottom: 12 }}
          actions={<>
            <Chip c="ok">DÉCIDÉ À L’ÉTAPE 02A</Chip>
            <span className="mo" style={{ fontSize: 12 }}>{fmtEur(totalSco)}</span>
          </>}
        >
          <div className="scrollx">
            <table className="t">
              <thead>
                <tr>
                  <th>Machine</th><th>Monnayeur</th>
                  <th className="n">Dénominations</th><th>Décision</th>
                  <th className="n">Valorisation bancaire</th>
                </tr>
              </thead>
              <tbody>
                {sco.map((m) => {
                  const nb = Object.keys(objet(m.rouleaux)).length + Object.keys(objet(m.billets)).length
                  return (
                    <tr key={String(m.caisse)}>
                      <td><b>SCO {String(m.caisse)}</b></td>
                      <td className="mo">{m.monnayeur == null ? '—' : String(m.monnayeur)}</td>
                      <td className="n">{nb}</td>
                      <td>
                        {m.ajuste === true
                          ? <Chip c="act">REPRISE À LA MAIN</Chip>
                          : <span className="hint">proposition suivie</span>}
                      </td>
                      <td className="n mo">{fmtEur(Number(m.valeur) || 0)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="hint" style={{ padding: '8px 12px', borderTop: '1px solid var(--line)' }}>
            Ces lignes se modifient à l’étape 02A, pas ici : elles y sont contrôlées contre le
            ticket de vérification du tiroir. Elles s’impriment sur le papier d’apport ci-dessous.
          </div>
        </Card>
      )}

      <Card
        titre="Apports du jour"
        lampe={vide ? 'warn' : lampeEcartUnites(manque)}
        actions={(
          <>
            {(!vide || sco.length > 0) && (
              <button className="btn sm" onClick={imprimerPapier}
                      title="Le formulaire d’entreprise : quatre colonnes de signature au recto, composition au verso">
                ⎙ Papier d’apport
              </button>
            )}
            {!lectureSeule && !vide && (
              <button className="btn pri sm" onClick={ouvrirChoix}>Servir un caissier</button>
            )}
          </>
        )}
      >
        {vide
          ? (
            <Vide>
              Aucun apport saisi. Rien n’a encore été remis aux caissiers ce matin.
              {!lectureSeule && (
                <div style={{ marginTop: 12 }}>
                  <button className="btn pri lg" onClick={ouvrirChoix}>Servir un caissier</button>
                </div>
              )}
            </Vide>
          )
          : (
            <div className="scrollx">
              <table className="t">
                <thead>
                  <tr>
                    <th>Caissier</th><th>Badge</th><th>Plomb</th><th>ORIKA (exception)</th>
                    <th className="n">Dénominations</th><th className="n">Valorisation bancaire</th>
                    <th style={{ width: 210 }} />
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l) => (
                    <tr key={l.cle}>
                      <td>
                        <b>{l.cle}</b>
                        {l.sansFdc && <> <Chip c="warn">SANS ESPÈCES</Chip></>}
                        {/* Un apport dont le caissier a disparu des réglages perd son
                            badge et son contrôle « SANS ESPÈCES » : il faut le dire. */}
                        {reglagesCharges && l.badge === null
                          && <> <Chip c="warn">INCONNU AUX RÉGLAGES</Chip></>}
                      </td>
                      <td className="mo" style={{ fontSize: 11, color: 'var(--ink3)' }}>{l.badge ?? '—'}</td>
                      <td className="mo" style={{ fontSize: 11, color: l.plomb ? 'var(--ink)' : 'var(--ink4)' }}>
                        {l.plomb || '—'}
                      </td>
                      <td>
                        {l.saisie === 'OK'
                          ? <Chip c="act">SAISI DANS ORIKA</Chip>
                          : <span className="hint">—</span>}
                      </td>
                      <td className="n" style={{ color: 'var(--ink3)' }}>{l.denoms}</td>
                      <td className="n">{fmtEur(l.total)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn sm" onClick={() => ouvrirSaisie(l.cle)}>
                          {lectureSeule ? 'Voir l’apport' : 'Reprendre la saisie'}
                        </button>
                        {!lectureSeule && <>
                          {' '}
                          <button className="btn stop sm" onClick={() => setARetirer(l.cle)}>Retirer</button>
                        </>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5}><b>Total du jour</b></td>
                    <td className="n"><b>{fmtEur(totalJour)}</b></td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(316px,1fr))', gap: 14, marginTop: 12 }}>
        <Card titre="Message porté sur la feuille d’apport">
          <div style={{ padding: '10px 12px' }}>
            <textarea
              className="fld" rows={3} value={message} disabled={lectureSeule}
              placeholder={lectureSeule ? '—' : 'Consigne ou remarque à faire figurer sur le document remis…'}
              style={{ resize: 'vertical' }}
              onChange={(e) => saisirMessage(e.target.value)}
              onBlur={poserMessage}
              onKeyDown={(e) => e.stopPropagation()}
            />
            <div className="hint" style={{ marginTop: 6 }}>
              Écrit dans <span className="mo">rapport.apportsMessage</span> — c’est ce texte que
              reprend la feuille d’apport imprimée.
            </div>
          </div>
        </Card>

        <Card titre="Fonds de caisse fixes">
          {caisseFixes.length === 0
            ? <Vide>Aucun fonds fixe déclaré dans les réglages SotCP.</Vide>
            : (
              <>
                {caisseFixes.map((f) => (
                  <div className="slot" key={f.id}>
                    <span className="nm">{f.name}</span>
                    <span className="sp" />
                    <span className="mo">{fmtEur(Number(f.default_amount) || 0)}</span>
                  </div>
                ))}
                <div className="slot">
                  <span className="nm">Total des fonds fixes</span>
                  <span className="sp" />
                  <span className="mo"><b>{fmtEur(totalFixes)}</b></span>
                </div>
              </>
            )}
          <div className="hint" style={{ padding: '8px 12px', borderTop: '1px solid var(--line)' }}>
            Montants de référence lus dans les réglages SotCP, jamais écrits d’ici. Ils ne se
            saisissent pas dans un apport : ils sont recomptés à l’étape 10, dans l’inventaire
            de coffre.
          </div>
        </Card>
      </div>

      <div style={{ marginTop: 12 }}>
        <Chantier titre="Feuille d’apport imprimée">
          L’export PDF de la V2 (<span className="mo">/api/generate-apports-pdf</span> : formulaire
          A4 paysage, matrice des dénominations, quatre colonnes de signature — Caisse Principale,
          Contrôle CDC, Titulaire, CDC Remettant) est une route serveur Next. FlashCP est une SPA
          statique sans processus Node : elle ne peut pas la produire. La feuille se tire depuis
          SotCP · Apports, sur les mêmes chiffres — ils sont écrits dans la même journée.
        </Chantier>
      </div>

      {aRetirer && (
        <Modale
          titre="Retirer cet apport ?"
          sous={`L’apport de ${aRetirer} — ${fmtEur(totalApport(objet(apports[aRetirer])))} — sera effacé de la journée. Le geste est tracé, mais les quantités saisies ne se retrouvent pas.`}
          fermer={() => { if (!travail) setARetirer(null) }}
        >
          <div className="btns">
            <button className="btn" disabled={travail} onClick={() => setARetirer(null)}>Annuler</button>
            <button className="btn stop" data-encours={travail ? 'true' : undefined} onClick={retirer}>
              Retirer l’apport
            </button>
          </div>
        </Modale>
      )}
    </>
  )
}

/** Remettre un apport à zéro efface un comptage : ça se confirme. */
function ModaleEffacer({ cle, travail, fermer, confirmer }: {
  cle: string; travail: boolean; fermer: () => void; confirmer: () => void
}) {
  return (
    <Modale
      titre="Effacer la saisie de cet apport ?"
      sous={`Les rouleaux, les billets, le plomb et la saisie ORIKA de ${cle} repartent à zéro. L’apport reste dans la journée — ce qui a été compté, non.`}
      fermer={() => { if (!travail) fermer() }}
    >
      <div className="btns">
        <button className="btn" disabled={travail} onClick={fermer}>Annuler</button>
        <button className="btn stop" data-encours={travail ? 'true' : undefined} onClick={confirmer}>
          Effacer la saisie
        </button>
      </div>
    </Modale>
  )
}

/** Une dénomination : son libellé, sa valorisation unitaire, sa quantité, son montant. */
function LigneDenom({ etiquette, sous, unite, quantite, changer, poser, lectureSeule, auto = false }: {
  etiquette: string
  sous: string
  unite: number
  quantite: number
  changer: (v: number) => void
  poser: () => void
  lectureSeule: boolean
  auto?: boolean
}) {
  const servi = quantite > 0
  return (
    <div className="slot">
      <Lamp c={servi ? 'act' : 'neutre'} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="nm">{etiquette}</div>
        <div className="hint mo">{sous}</div>
      </div>
      {lectureSeule
        ? <span className="mo" style={{ minWidth: 34, textAlign: 'right' }}>{quantite}</span>
        : <Compteur valeur={quantite} changer={changer} poser={poser} auto={auto} />}
      <span className="mo" style={{
        minWidth: 82, textAlign: 'right', color: servi ? 'var(--ink)' : 'var(--ink4)',
      }}>
        {fmtEur(quantite * unite)}
      </span>
    </div>
  )
}

/**
 * Entrée passe au compteur suivant : compter dix dénominations puis le plomb ne
 * doit pas demander onze allers-retours à la souris.
 *
 * Le dernier champ garde la main. L'appelant a déjà posé la saisie ; lui
 * retirer le focus en plus renverrait le curseur sur <body>, d'où la suite du
 * geste — la question ORIKA — se paie en Tab depuis le haut du document.
 */
function champSuivant(courant: HTMLInputElement): void {
  const zone = courant.closest('[data-compteurs]')
  const tous = Array.from(zone?.querySelectorAll<HTMLInputElement>('input[data-compteur]') ?? [])
  const i = tous.indexOf(courant)
  const suivant = i >= 0 ? tous[i + 1] : undefined
  if (suivant) { suivant.focus(); suivant.select() }
}

/** − / quantité / + . Le champ est saisissable : servir 40 rouleaux ne se fait pas au clic. */
function Compteur({ valeur, changer, poser, auto = false }: {
  valeur: number
  changer: (v: number) => void
  poser: () => void
  auto?: boolean
}) {
  // Tampon de frappe : il autorise le champ vide le temps de retaper un nombre.
  // Sans lui, effacer « 12 » réaffichait « 0 » sous les doigts et il fallait
  // retaper par-dessus. La valeur, elle, vaut bien 0 dans le total.
  const [tampon, setTampon] = useState<string | null>(null)
  const avant = useRef(valeur)

  const saisir = (brut: string) => {
    // Quatre chiffres : 9 999 rouleaux de 2 € valent un demi-million d'euros.
    // Au-delà ce n'est plus une quantité, c'est une faute de frappe.
    const s = brut.replace(/\D/g, '').slice(0, 4)
    setTampon(s)
    changer(s === '' ? 0 : entier(s))
  }

  // Les deux boutons sortent du parcours de tabulation : Tab passe ainsi d'une
  // dénomination à la suivante, pas de bouton en bouton.
  const pas = (delta: number) => { setTampon(null); changer(Math.max(0, valeur + delta)) }

  return (
    <div style={{ display: 'inline-flex', alignItems: 'stretch' }}>
      {/* Le ton rend le geste lisible d'un coup d'œil : on ne lit pas « + » et
          « − » à la vitesse où on sert dix dénominations, on lit deux couleurs. */}
      <button className="btn stop sm" disabled={valeur === 0} aria-label="Un de moins" tabIndex={-1}
              style={{ borderRadius: 'var(--r-s) 0 0 var(--r-s)' }}
              onClick={() => pas(-1)}>−</button>
      <input
        className="mo" inputMode="numeric" data-compteur autoFocus={auto}
        value={tampon ?? String(valeur)} aria-label="Quantité"
        onChange={(e) => saisir(e.target.value)}
        onFocus={(e) => { avant.current = valeur; e.currentTarget.select() }}
        onBlur={() => { setTampon(null); poser() }}
        onKeyDown={(e) => {
          e.stopPropagation()
          const el = e.currentTarget
          if (e.key === 'Enter') { setTampon(null); poser(); champSuivant(el) }
          else if (e.key === 'Escape') { changer(avant.current); setTampon(null); el.blur() }
        }}
        style={{
          width: 52, textAlign: 'center', padding: '4px 2px', fontSize: 12.5,
          border: '1px solid var(--line2)', borderLeft: 'none', borderRight: 'none',
          background: 'var(--surface)',
        }}
      />
      <button className="btn ok sm" aria-label="Un de plus" tabIndex={-1}
              style={{ borderRadius: '0 var(--r-s) var(--r-s) 0' }}
              onClick={() => pas(1)}>+</button>
    </div>
  )
}
